import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Stack, Box, Typography, Collapse, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { KINDS, KIND_LABEL, STATUSES } from './constants';
import { useCreateProject, useUpdateProject } from '../../hooks/useProjects';
import { useRoster } from '../../hooks/useRoster';

/**
 * Create/Edit project dialog.
 * Props:
 *   open
 *   onClose
 *   initial      — project row when editing; undefined/null when creating
 *   defaultParentId  — pre-selected parent when creating a sub-project
 *   flatProjects — all projects (for parent picker)
 *   onSaved(project) — optional callback with created/updated project
 */
export default function ProjectFormDialog({ open, onClose, initial, defaultParentId, flatProjects = [], onSaved }) {
  const isEdit = !!initial;
  const createMut = useCreateProject();
  const updateMut = useUpdateProject();
  const { data: roster = [] } = useRoster();

  const [form, setForm] = useState({
    name: '', kind: 'other', parent_id: '', owner_id: '', status: 'active',
    description: '', hero_image_url: '', gdrive_folder_url: '',
    starts_at: '', ends_at: '',
    production_slug: '', budget_hours: '', budget_dollars: '',
    // Segment-only structured fields (drive auto-derived name)
    week: '', property_number: '', property_name: '',
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const data = initial.data || {};
      setForm({
        name: initial.name || '',
        kind: initial.kind || 'other',
        parent_id: initial.parent_id || '',
        owner_id: initial.owner_id || '',
        status: initial.status || 'active',
        description: initial.description || '',
        hero_image_url: initial.hero_image_url || '',
        gdrive_folder_url: initial.gdrive_folder_url || '',
        starts_at: initial.starts_at ? String(initial.starts_at).slice(0, 10) : '',
        ends_at: initial.ends_at ? String(initial.ends_at).slice(0, 10) : '',
        production_slug: initial.production_slug || '',
        budget_hours: initial.budget_hours ?? '',
        budget_dollars: initial.budget_dollars ?? '',
        week: data.week ?? '',
        property_number: data.propertyNumber ?? '',
        property_name: data.propertyName || '',
      });
    } else {
      setForm({
        name: '', kind: 'other', parent_id: defaultParentId || '', owner_id: '', status: 'active',
        description: '', hero_image_url: '', gdrive_folder_url: '',
        starts_at: '', ends_at: '',
        production_slug: '', budget_hours: '', budget_dollars: '',
        week: '', property_number: '', property_name: '',
      });
    }
  }, [open, isEdit, initial, defaultParentId]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Find the season ancestor name (walks parent chain)
  const seasonName = useMemo(() => {
    let cur = flatProjects.find((p) => p.id === form.parent_id);
    while (cur) {
      if (cur.kind === 'season') return cur.name;
      cur = flatProjects.find((p) => p.id === cur.parent_id);
    }
    return null;
  }, [flatProjects, form.parent_id]);

  // Live preview of derived segment name
  const segmentPreview = useMemo(() => {
    if (form.kind !== 'segment') return null;
    if (form.week === '' || form.property_number === '' || !form.property_name) return null;
    const prefix = seasonName ? `${seasonName} \u2014 ` : '';
    return `${prefix}W${form.week}P${form.property_number} ${form.property_name}`;
  }, [form.kind, form.week, form.property_number, form.property_name, seasonName]);

  const parentOptions = flatProjects
    .filter((p) => !isEdit || p.id !== initial.id) // can't set self as parent when editing
    .map((p) => ({ id: p.id, label: `${'  '.repeat(p.depth || 0)}${p.name}` }));

  const submit = async () => {
    // Auto-derive segment name from week/property fields if all provided
    let computedName = form.name;
    let computedData = isEdit && initial?.data ? { ...initial.data } : {};
    if (form.kind === 'segment' && form.week !== '' && form.property_number !== '' && form.property_name) {
      const w = Number(form.week);
      const pn = Number(form.property_number);
      const prefix = seasonName ? `${seasonName} \u2014 ` : '';
      computedName = `${prefix}W${w}P${pn} ${form.property_name}`;
      computedData = { ...computedData, week: w, propertyNumber: pn, propertyName: form.property_name };
    }

    const body = {
      name: computedName,
      kind: form.kind,
      parent_id: form.parent_id || null,
      owner_id: form.owner_id || null,
      status: form.status,
      description: form.description,
      hero_image_url: form.hero_image_url,
      gdrive_folder_url: form.gdrive_folder_url,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      production_slug: form.production_slug || null,
      budget_hours: form.budget_hours === '' ? null : Number(form.budget_hours),
      budget_dollars: form.budget_dollars === '' ? null : Number(form.budget_dollars),
      data: computedData,
    };
    let saved;
    if (isEdit) saved = await updateMut.mutateAsync({ id: initial.id, ...body });
    else saved = await createMut.mutateAsync(body);
    onSaved?.(saved);
    onClose();
  };

  const busy = createMut.isPending || updateMut.isPending;

  const isSegment = form.kind === 'segment';
  const isProductionKind = ['season', 'episode', 'segment'].includes(form.kind);
  const canSave = isSegment
    ? !!segmentPreview || !!form.name.trim()
    : !!form.name.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Project' : 'New Project'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Kind + Status — always shown first */}
          <Stack direction="row" spacing={2}>
            <TextField select label="Kind" value={form.kind} onChange={onChange('kind')} sx={{ flex: 1 }}>
              {KINDS.map((k) => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={form.status} onChange={onChange('status')} sx={{ flex: 1 }}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
            </TextField>
          </Stack>

          {/* Parent picker */}
          <TextField select label="Parent" value={form.parent_id} onChange={onChange('parent_id')} fullWidth>
            <MenuItem value="">(none — root)</MenuItem>
            {parentOptions.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
          </TextField>

          {/* Segment: structured inputs (auto-derive name) */}
          {isSegment ? (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Week" type="number" value={form.week} onChange={onChange('week')} sx={{ width: { xs: '100%', sm: 100 } }} inputProps={{ min: 0 }} required />
                <TextField label="Property #" type="number" value={form.property_number} onChange={onChange('property_number')} sx={{ width: { xs: '100%', sm: 130 } }} inputProps={{ min: 0 }} helperText="0 = throughlines" required />
                <TextField label="Property name" value={form.property_name} onChange={onChange('property_name')} sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }} required autoFocus />
              </Stack>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Auto-named:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
                  {segmentPreview || <em style={{ color: '#888' }}>Fill week / property # / property name…</em>}
                </Typography>
              </Box>
              <TextField
                label="Production slug"
                value={form.production_slug}
                onChange={onChange('production_slug')}
                fullWidth
                helperText="Short token used to link voicenotes & outlines (e.g. munaya, springdale)"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Budget (hrs)"
                  type="number"
                  value={form.budget_hours}
                  onChange={onChange('budget_hours')}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  label="Budget ($)"
                  type="number"
                  value={form.budget_dollars}
                  onChange={onChange('budget_dollars')}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Stack>
            </>
          ) : (
            <TextField label="Name" value={form.name} onChange={onChange('name')} fullWidth autoFocus required />
          )}

          {/* Owner — relevant for all kinds */}
          <TextField select label="Owner" value={form.owner_id} onChange={onChange('owner_id')} fullWidth>
            <MenuItem value="">(none)</MenuItem>
            {roster.filter((r) => r.profileId).map((r) => (
              <MenuItem key={r.profileId} value={r.profileId}>{r.name}</MenuItem>
            ))}
          </TextField>

          {/* Advanced toggle */}
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={() => setShowAdvanced((v) => !v)}
            sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13, color: 'text.secondary' }}
          >
            {showAdvanced ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            {showAdvanced ? 'Hide advanced' : 'Show advanced (dates, links, description)'}
          </Link>

          <Collapse in={showAdvanced}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField label="Starts" type="date" value={form.starts_at} onChange={onChange('starts_at')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                <TextField label="Ends" type="date" value={form.ends_at} onChange={onChange('ends_at')} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
              </Stack>
              <TextField label="Gdrive folder URL" value={form.gdrive_folder_url} onChange={onChange('gdrive_folder_url')} fullWidth />
              <TextField label="Hero image URL" value={form.hero_image_url} onChange={onChange('hero_image_url')} fullWidth />
              <TextField label="Description" value={form.description} onChange={onChange('description')} fullWidth multiline rows={3} />
              {!isSegment && isProductionKind && (
                <TextField
                  label="Production slug"
                  value={form.production_slug}
                  onChange={onChange('production_slug')}
                  fullWidth
                  helperText="Optional — links season/episode to voicenotes & outlines"
                />
              )}
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !canSave}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
